import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpEvent,
  HttpRequest,
  HttpHandler,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { HttpResponse } from '@angular/common/http';

import { environment } from './../../environments/environment';
import { StorageService } from '../services/storage.service';
import { StorageKey } from '../models/common';
import { ToasterService } from '../utils/toaster.service';
import {
  ApiErrorMessages,
  getApiErrorMessage,
  isInviteVerifyRequestUrl,
  OMIT_COMPANY_HEADER,
  parseApiErrorBody,
  SKIP_AUTH_HEADER,
  SUPPRESS_ERROR_TOAST_HEADER,
} from '../utils/api-error.utils';
import { Helpers } from '../utils/helpers-util';
import { TokenRefreshService } from '../services/token-refresh.service';
import { CompanyHeaderOverrideService } from '../services/company-header-override.service';
import { ROLE_ID_TO_NAME_MAP } from '../utils/role-id-to-name.map';
import * as crypto from 'crypto-js';

@Injectable()
export class CustomHttpInterceptor implements HttpInterceptor {
  private readonly _appType: string = 'Broker';
  /**
   * When a token refresh fails (both access + refresh token expired — the case
   * after a long idle period), every in-flight request that triggered the
   * refresh calls handleLogout() independently. Without this guard, several
   * near-simultaneous router.navigateByUrl('/login') calls race the Router's
   * current transition and can leave it stuck, so later clicks silently do
   * nothing until a full reload.
   */
  private loggingOut = false;

  constructor(
    private storageSvc: StorageService,
    private router: Router,
    private toasterService: ToasterService,
    private tokenRefreshSvc: TokenRefreshService,
    private companyOverride: CompanyHeaderOverrideService
  ) { }

  intercept(httpRequest: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (httpRequest.url.includes('/v1/User/refresh/jwt')) {
      return next.handle(httpRequest);
    }

    const skipAuth = httpRequest.headers.get(SKIP_AUTH_HEADER) === 'true';
    const token = skipAuth ? null : this.storageSvc.get(StorageKey.jwt);

    let url: string = httpRequest.url;
    if (!url.startsWith('http') && !url.startsWith('/v1/agent')) {
      url = `${environment.apiUrl}${httpRequest.url}`;
    }

    const apiKeyStr = `${environment.encryptionKey}#${this.getCurrentDateTimeInISOFormat()}`;
    const key = crypto.enc.Base64.parse(environment.encryptionKey);
    const encrypted = crypto.AES.encrypt(apiKeyStr, key, {
      mode: crypto.mode.ECB,
      padding: crypto.pad.Pkcs7
    });
    const base64CipherText = encrypted.ciphertext.toString(crypto.enc.Base64);

    let headers = httpRequest.headers.set('ApiKey', base64CipherText);

    const omitCompany = httpRequest.headers.get(OMIT_COMPANY_HEADER) === 'true';
    headers = headers.delete(OMIT_COMPANY_HEADER);

    if (token) {
      const roleId = localStorage.getItem('selectedRoleId') || '';
      const roleName = ROLE_ID_TO_NAME_MAP[roleId] || '';
      headers = headers
        .set('Authorization', `Bearer ${token}`)
        .set('AppType', this._appType)
        .set('Role', roleId)
        .set('RoleName', roleName);
      if (!omitCompany) {
        // A request may target another organization explicitly (System Admin creating an org admin).
        headers = headers.set(
          'Company',
          httpRequest.headers.get('Company') ||
            this.companyOverride.companyId ||
            localStorage.getItem('selectedCompanyId') ||
            ''
        );
      }
    }

    // Don't set Content-Type for FormData — browser auto-sets multipart/form-data with boundary
    if (!(httpRequest.body instanceof FormData)) {
      headers = headers.set('Content-Type', 'application/json');
    }

    const req = httpRequest.clone({ url, headers });

    return next.handle(req).pipe(
      map((event: HttpEvent<any>) => {
        // Transform amounts in successful HTTP responses
        if (event instanceof HttpResponse && event.body) {
          const transformedBody = Helpers.transformAmountsInObject(event.body);
          return event.clone({ body: transformedBody });
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        const parsedErrorBody = parseApiErrorBody(error);
        const exception =
          (parsedErrorBody?.['title'] as string | undefined) ?? error?.error?.title;
        const status = error?.status;

        if (
          exception === 'ExpiredJwtException' &&
          (status === 400 || status === 500)
        ) {
          return this.handleExpiredJwt(req, next);
        }

        if (
          exception === 'JwtTokenMissingException' ||
          exception === 'JwtUserNotFoundException' ||
          exception === 'JWTUserRoleNotFoundException'
        ) {
          let message = '';
          if (exception === 'JwtUserNotFoundException') {
            message = ApiErrorMessages.invalidUser;
          } else if (exception === 'JWTUserRoleNotFoundException') {
            message = ApiErrorMessages.roleRemoved;
          } else {
            message = getApiErrorMessage(error, ApiErrorMessages.unauthorized);
          }

          this.handleLogout(message);
          return throwError(() => error);
        }

        // Centralized error handling: show toast for all API errors unless suppressed
        const suppressToastExceptions = new Set([
          'InvoiceNotFoundException',
          'GroupNotFoundException',
          'CompanyNotFoundException',
          'StoreNotFoundException',
          'UserNotFoundException',
          'StoreInGroupNotFoundException',
          'RoleNotFoundException',
          'StoreCategoryNotFoundException',
          // Duplicate check for dedicated emails: "not found" means the email is free.
          'DedicatedEmailNotFoundException',
          // Shown inline on the login page instead.
          'OrganizationInactiveException',
        ]);
        const errorMessage = getApiErrorMessage(error);
        const isDisabledMessage =
          errorMessage && errorMessage.trim().toLowerCase() === 'disabled';
        const suppressInviteVerifyFailure =
          isInviteVerifyRequestUrl(req.url) &&
          (status === 400 || status === 404);

        const suppressToast =
          req.headers.get(SUPPRESS_ERROR_TOAST_HEADER) === 'true' ||
          // `title` may be the short name or the full type name (Core.Models.Exceptions.X).
          (exception && suppressToastExceptions.has(exception.split('.').pop() ?? exception)) ||
          isDisabledMessage ||
          suppressInviteVerifyFailure;
        if (!suppressToast) {
          this.toasterService.showError(errorMessage);
        }

        return throwError(() => error);
      })
    );
  }

  private handleExpiredJwt(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.tokenRefreshSvc.refreshAccessToken().pipe(
      switchMap(({ token }) => {
        const clonedReq = req.clone({
          headers: req.headers.set('Authorization', `Bearer ${token}`)
        });
        return next.handle(clonedReq);
      }),
      catchError((err) => {
        const message =
          err?.message === ApiErrorMessages.noRefreshToken
            ? ApiErrorMessages.noRefreshToken
            : ApiErrorMessages.sessionExpired;
        this.handleLogout(message);
        return throwError(() => err);
      })
    );
  }


  private handleLogout(message: string) {
    if (this.loggingOut) {
      return;
    }
    this.loggingOut = true;
    this.toasterService?.showError(message, ApiErrorMessages.unauthorized);
    sessionStorage.clear();
    localStorage.clear();
    this.router?.navigateByUrl('/login').finally(() => {
      this.loggingOut = false;
    });
  }

  private getCurrentDateTimeInISOFormat(): string {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    const hours = String(now.getUTCHours()).padStart(2, '0');
    const minutes = String(now.getUTCMinutes()).padStart(2, '0');
    const seconds = String(now.getUTCSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
  }
}
